import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Briefcase, Buildings, Calendar, DocumentText1, Edit2, Timer1, Trash, UserEdit } from "iconsax-react";
import { STATUS_MAP } from "@/features/hr/employees/consts/statuses";
import { formatTrialPeriodEnd } from "@/features/hr/employees/utils/trialPeriod";
import { TengeCircleIcon } from "@/shared/assets/icons";
import { Badge, Button } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { formatDateForDisplay, formatMoneyKzt, type Locale } from "@/shared/utils";
import type { WorkerListItem, ImportWorkerDraftListItem } from "../../../types";
import EmployeeNameCell from "./EmployeeNameCell";
import EmployeeAccessRoleIcons from "./EmployeeAccessRoleIcons";
import DraftNameCell from "./DraftNameCell";

interface Props {
  employees: WorkerListItem[];
  importDrafts?: ImportWorkerDraftListItem[];
  onDownloadProfile: (id: number) => void;
  isDownloading: boolean;
  onOpenAssignRole: (employee: WorkerListItem) => void;
  onRehire: (employee: WorkerListItem) => void;
  isRehiring: boolean;
  onSelectEmployee: (employee: WorkerListItem) => void;
  onDeleteDraft?: (id: number) => void;
}

export default function EmployeesCardsView({
  employees,
  importDrafts,
  onDownloadProfile,
  isDownloading,
  onOpenAssignRole,
  onRehire,
  isRehiring,
  onSelectEmployee,
  onDeleteDraft,
}: Props) {
  const { t, i18n } = useTranslation("EmployeesPage");
  const locale = (i18n.language as Locale) || "ru";
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
      {importDrafts?.map(function renderDraftCard(draft) {
        const position = locale === "kk" ? (draft.job_position_kk || draft.job_position_ru) : draft.job_position_ru;
        return (
          <div key={`draft-${draft.id}`} className="flex flex-col justify-between surface-component-fill radius-lg p-5">
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/hr/import-worker?draftId=${draft.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/hr/import-worker?draftId=${draft.id}`); } }}
              className="cursor-pointer flex flex-col gap-3">
              <div className="flex flex-col gap-3 pb-4 border-b surface-base-stroke">
                <Badge
                  variant="soft"
                  color="gray"
                  text={t("status.draft")}
                  icon={<Edit2 size={14} color="currentColor" />}
                />
                <DraftNameCell draft={draft} />
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="content-base-secondary text-body-bold-xs">{t("table.position")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="content-action-brand">
                    <Briefcase size={18} color="currentColor" />
                  </span>
                  <p className="text-body-regular-sm content-base-primary">{position || "—"}</p>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="content-base-secondary text-body-bold-xs">{t("cards.salary")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="content-action-brand">
                    <TengeCircleIcon size={18} />
                  </span>
                  <p className="text-body-regular-sm content-base-primary">—</p>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="content-base-secondary text-body-bold-xs">{t("cards.hireDate")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="content-action-brand">
                    <Calendar size={18} color="currentColor" />
                  </span>
                  <p className="text-body-regular-sm content-base-primary">—</p>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 pb-3">
                <p className="content-base-secondary text-body-bold-xs">{t("table.role")}</p>
                <p className="text-body-regular-sm content-base-primary">—</p>
              </div>
            </div>

            {onDeleteDraft && (
              <div className="flex flex-col gap-2 pt-3 border-t surface-base-stroke">
                <Button
                  variant="secondary"
                  size="md"
                  className="w-full"
                  onClick={() => navigate(`/hr/import-worker?draftId=${draft.id}`)}>
                  <Edit2 size={16} color="currentColor" />
                  {t("draft.editAction")}
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  className="w-full"
                  onClick={() => onDeleteDraft(draft.id)}>
                  <Trash size={16} color="currentColor" />
                  {t("draft.deleteAction")}
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {employees.map(function renderCard(emp) {
        const salaryNum = Number(emp.active_contract?.salary_amount ?? 0);
        const salaryFormatted = salaryNum ? formatMoneyKzt(salaryNum, locale) : "—";
        const statusConfig = STATUS_MAP[emp.status as keyof typeof STATUS_MAP];
        const isResigned = emp.status === "resigned";
        const canRehire = isResigned && hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);
        const canDownload = !isResigned;
        const canAssignRole = !isResigned && hasPermission(PERMISSIONS.ADMIN.MANAGE_ROLES) && !!emp.user_id;
        const hasAnyAction = canRehire || canDownload || canAssignRole;

        return (
          <div key={emp.id} className="flex flex-col justify-between surface-component-fill radius-lg p-5">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onSelectEmployee(emp)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectEmployee(emp); } }}
              className="cursor-pointer flex flex-col gap-3">
              <div className="flex flex-col gap-3 pb-4 border-b surface-base-stroke">
                {statusConfig && (
                  <Badge
                    variant="soft"
                    color={statusConfig.color}
                    text={t(statusConfig.label)}
                    icon={statusConfig.icon}
                  />
                )}
                <EmployeeNameCell employee={emp} />
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="content-base-secondary text-body-bold-xs">{t("table.position")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="content-action-brand">
                    <Briefcase size={18} color="currentColor" />
                  </span>
                  <p className="text-body-regular-sm content-base-primary">
                    {emp.active_contract?.job_position_ru || emp.active_contract?.work_position || "—"}
                  </p>
                </div>
              </div>

              {emp.department && (
                <div className="flex flex-col gap-1.5">
                  <p className="content-base-secondary text-body-bold-xs">{t("table.department")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Buildings size={18} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">{emp.department.name_ru}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <p className="content-base-secondary text-body-bold-xs">{t("cards.salary")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="content-action-brand">
                    <TengeCircleIcon size={18} />
                  </span>
                  <p className="text-body-regular-sm content-base-primary">{salaryFormatted}</p>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="content-base-secondary text-body-bold-xs">{t("cards.hireDate")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="content-action-brand">
                    <Calendar size={18} color="currentColor" />
                  </span>
                  <p className="text-body-regular-sm content-base-primary">
                    {emp.active_contract?.start_date ? formatDateForDisplay(emp.active_contract.start_date) : "—"}
                  </p>
                </div>
              </div>

              {emp.trial_period_end_date && (
                <div className="flex flex-col gap-1.5">
                  <p className="content-base-secondary text-body-bold-xs">{t("cards.trialPeriod")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Timer1 size={18} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">
                      {formatTrialPeriodEnd(emp.trial_period_end_date, t)}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5 pb-3">
                <p className="content-base-secondary text-body-bold-xs">{t("table.role")}</p>
                <EmployeeAccessRoleIcons roles={emp.roles ?? []} locale={locale} forceBaseFill tooltipAlign="left" />
              </div>
            </div>

            {hasAnyAction && (
              <div className="flex flex-col gap-2 pt-3 border-t surface-base-stroke">
                {canRehire && (
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full"
                    disabled={isRehiring}
                    onClick={() => onRehire(emp)}>
                    {isRehiring ? t("rehire.loading") : t("rehire.button")}
                  </Button>
                )}
                {canDownload && (
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full"
                    disabled={isDownloading}
                    onClick={() => onDownloadProfile(emp.id)}>
                    <DocumentText1 size={16} color="currentColor" />
                    {t("cards.downloadProfile")}
                  </Button>
                )}
                {canAssignRole && (
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full"
                    onClick={() => onOpenAssignRole(emp)}>
                    <UserEdit size={16} color="currentColor" />
                    {t("roleAssignment.assignRole")}
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
